import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { PageHeader } from "~/components/ui/page-title";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { organizationInvitePageQueryOptions } from "~/lib/functions/organization-invites";
import {
  buildInviteMessage,
  buildOrganizationInviteUrl,
} from "~/lib/shared/organization-invite";

/** Select values: the organization alone, or `program:<slug>`. */
const MEMBERSHIP = "membership";
const programValue = (slug: string): string => `program:${slug}`;

export const Route = createFileRoute("/_user/my-organization/$slug/invite")({
  staticData: { title: "Invite graduates" },
  loader: async ({ context, params }) => {
    const page = await context.queryClient.ensureQueryData(
      organizationInvitePageQueryOptions(params.slug),
    );
    // Closed while Organization verification is off, or for someone who doesn't represent
    // the organization.
    if (!page) throw notFound();
  },
  component: InviteGraduatesPage,
});

async function copy(text: string, what: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error(
      `Couldn't copy the ${what.toLowerCase()}. Select it and copy it instead.`,
    );
  }
}

function InviteGraduatesPage(): React.JSX.Element | null {
  const { slug } = Route.useParams();
  const { data: page } = useSuspenseQuery(
    organizationInvitePageQueryOptions(slug),
  );
  const [credential, setCredential] = useState<string>(
    page?.programs[0] ? programValue(page.programs[0].slug) : MEMBERSHIP,
  );
  if (!page) return null;

  const { organization, programs } = page;
  const program =
    programs.find((p) => programValue(p.slug) === credential) ?? null;
  const link = buildOrganizationInviteUrl({
    siteUrl: page.siteUrl,
    organizationSlug: organization.slug,
    programSlug: program?.slug ?? null,
  });
  const message = buildInviteMessage({
    organizationName: organization.name,
    programName: program?.name ?? null,
    link,
  });
  const messageText = `Subject: ${message.subject}\n\n${message.body}`;
  const count = page.invitedPublicCoaches;

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={organization.name}
        title="Invite your graduates to Dream Coach Match"
        description={`Send this link to graduates who are not on Dream Coach Match yet. When they join through it, their ${organization.name} credential is added to their profile and waits for you in Coach claims.`}
      />

      <Card className="space-y-6">
        <div className="space-y-1.5">
          <Label htmlFor="invite-credential">Credential to add</Label>
          <Select value={credential} onValueChange={setCredential}>
            <SelectTrigger id="invite-credential">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.slug} value={programValue(p.slug)}>
                  {p.name} (program)
                </SelectItem>
              ))}
              <SelectItem value={MEMBERSHIP}>
                Member of {organization.name}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invite-link">Your invite link</Label>
          <div className="flex gap-2">
            <Input
              id="invite-link"
              value={link}
              readOnly
              className="font-mono text-sm"
              onFocus={(event) => event.currentTarget.select()}
            />
            <Button variant="primary" onClick={() => void copy(link, "Link")}>
              Copy link
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-sm font-medium text-gray-900">
            Suggested message{" "}
            <span className="text-text-tertiary font-normal">
              · edit it before you send
            </span>
          </p>
          <div className="text-text-secondary space-y-3 rounded-lg border border-gray-200 p-4 text-sm whitespace-pre-line">
            <p>
              <span className="font-medium text-gray-900">Subject:</span>{" "}
              {message.subject}
            </p>
            <p>{message.body}</p>
          </div>
          <Button onClick={() => void copy(messageText, "Message")}>
            Copy message
          </Button>
        </div>

        <p className="rounded-lg border border-purple-100 bg-purple-50 p-3 text-sm text-purple-900">
          Dream Coach Match does not email your graduates. You send the link
          yourself, to people you already write to.
        </p>
      </Card>

      <Card className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-text-secondary text-sm">
          <span className="text-base font-medium text-gray-900">
            {count} {count === 1 ? "coach" : "coaches"}
          </span>{" "}
          joined through your link and {count === 1 ? "has" : "have"} a public
          profile
        </p>
        <Link
          to="/my-organization/$slug/claims"
          params={{ slug }}
          className="text-sm text-purple-700 underline underline-offset-2"
        >
          See them in Coach claims
        </Link>
      </Card>
    </div>
  );
}
